import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNodeByKey } from 'lexical';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import * as documentsSelectors from 'containers/Documents/selectors';
import { $fixTextNodeSelection } from '../../helpers/fixTextNodeSelection';
import { ReferenceCitationNode } from '../../nodes/ReferenceCitationNode';
import { ReferenceCitationSeparatorNode } from '../../nodes/ReferenceCitationSeparatorNode';
import { useEditModeContext } from '../EditModeContextPlugin';
import { createDropHandler } from './utils/dropHandler';
import { $reorderNodes } from './utils/reorderNodes';
import { $setNodeText, $setNodeTextWithNumbersRange } from './utils/setNodeText';

export { $applyReferenceCitationNumbering } from './utils/applyReferenceCitationNumbering';
export { $getReferenceCitationNodes } from './utils/getReferenceCitationNodes';

type ReferencesNode = ReferenceCitationNode | ReferenceCitationSeparatorNode;

type Props = {
  referencesMap: Record<string, number>;
  saveHandler: () => void;
};

export const ReferenceCitationPlugin = (props: Props): null => {
  const references = useSelector(documentsSelectors.citationsForAssetsPanel);
  const storyCardDocuments = useSelector(documentsSelectors.storyCardsAndMagicFormsDocuments);
  const { referencesMap, saveHandler } = props;
  const referencesMapRef = useRef(referencesMap);
  referencesMapRef.current = referencesMap;
  const saveHandlerRef = useRef(saveHandler);
  saveHandlerRef.current = saveHandler;

  const isMounted = useRef(false);

  const [editor] = useLexicalComposerContext();
  const editMode = useEditModeContext();
  const editModeRef = useRef(editMode);
  editModeRef.current = editMode;

  const root = editor.getRootElement();
  const nodesCache = useMemo<Set<string>>(
    () => new Set(),
    [editor], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const $updateReferenceNodes = useCallback((key: string): void => {
    const node = $getNodeByKey<ReferenceCitationNode>(key);
    if (!node) {
      return;
    }
    $reorderNodes(node, referencesMapRef.current);
    let mutatedNodes: ReferencesNode[];
    if (editModeRef.current) {
      mutatedNodes = $setNodeText(node, referencesMapRef.current);
    } else {
      mutatedNodes = $setNodeTextWithNumbersRange(node, referencesMapRef.current);
    }
    mutatedNodes.forEach($fixTextNodeSelection);
  }, []);

  useEffect(() => {
    if (!root) {
      return;
    }
    const dropHandler = createDropHandler(editor, references, storyCardDocuments, () => saveHandlerRef.current());
    root.addEventListener('drop', dropHandler);

    return () => root.removeEventListener('drop', dropHandler);
  }, [root, editor, storyCardDocuments, references]);

  useEffect(() => {
    if (!isMounted.current) {
      return;
    }
    editor.update(() => {
      nodesCache.forEach($updateReferenceNodes);
    });
  }, [referencesMap, editMode, editor, nodesCache, $updateReferenceNodes]);

  useEffect(() => {
    isMounted.current = true;

    return editor.registerMutationListener(
      ReferenceCitationNode,
      (mutatedNodes) => {
        for (const [key, mutation] of mutatedNodes) {
          if (mutation === 'created') {
            nodesCache.add(key);
            editor.update(() => $updateReferenceNodes(key));
          } else if (mutation === 'destroyed') {
            nodesCache.delete(key);
          }
        }
      },
      { skipInitialization: false },
    );
  }, [editor, nodesCache, $updateReferenceNodes]);

  return null;
};

